import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../../Common/Modal/Modal';
import { InputText } from 'primereact/inputtext';
import MultiSelectBox from '@Components/Common/MultiSelectBox/MultiSelectBox';
import Toggle from '@Components/Common/Toggle/Toggle';
import Notification from '../../../../../../Common/Notification';
import { useDispatch, useSelector } from 'react-redux';
import { actions as disciplineActions } from '../../../../../../../actions/disciplineActions';
import * as types from '../../../../../../../constants/actionTypes';

interface Department {
	id: number;
	name: string;
	isActive: boolean;
	subscriptionId: number;
	disciplineId: number;
}

interface AddDepartmentProps {
	onSave: (department: Department) => void;
	onCancel: () => void;
}

const AddDepartment: React.FC<AddDepartmentProps> = ({ onSave, onCancel }) => {
	const dispatch = useDispatch();
	const currentSubscriptionId = useSelector((state: { user: { currentSubscriptionId: number } }) => state.user.currentSubscriptionId);

	const [newDepartment, setNewDepartment] = useState<Department>({
		id: 0,
		name: '',
		isActive: true,
		subscriptionId: currentSubscriptionId || 0,
		disciplineId: 0,
	});
	const [disciplines, setDisciplines] = useState([]);
	const [isInputInvalid, setIsInputInvalid] = useState(false);

	const loadDisciplines = useCallback(async () => {
		try {
			const result = (await dispatch(disciplineActions.listDisciplines({ subscriptionId: currentSubscriptionId }))) as any;

			if (result.type === types.LIST_DISCIPLINE_SUCCESS) {
				setDisciplines(result.payload.disciplines);
			} else {
				Notification('error', 'An Error Occurred', result.message);
			}
		} catch (error) {
			Notification('error', 'Error', (error as any).message);
		}
	}, [dispatch, currentSubscriptionId]);

	useEffect(() => {
		loadDisciplines();
	}, [loadDisciplines]);

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewDepartment((prev) => ({ ...prev, name: e.target.value }));
	};

	const onDisciplineChange = (id: number) => {
		setNewDepartment((prev) => ({ ...prev, disciplineId: id }));
	};

	const handleSave = () => {
		if (!newDepartment?.name) {
			setIsInputInvalid(true);
			return;
		}
		setIsInputInvalid(false);
		onSave(newDepartment);
	};

	return (
		<Modal
			title='Add Department'
			body={
				<>
					<InputText
						id='name'
						name='name'
						placeholder='Name'
						invalid={isInputInvalid}
						type='text'
						value={newDepartment?.name}
						onChange={handleOnChange}
						maxLength={175}
						className='mb-3'
					/>
					<MultiSelectBox
						name='discipline'
						label='Discipline'
						idField='id'
						valueField='name'
						options={disciplines}
						selectedValues={newDepartment.disciplineId ? newDepartment.disciplineId.toString() : ''}
						onChange={onDisciplineChange}
						isRequired={true}
						errorMessage='Discipline is required'
						isMulti={false}
					/>
					<Toggle
						name='isActive'
						text='Active'
						checked={newDepartment.isActive}
						onChange={(e: { target: { checked: boolean } }) => setNewDepartment((prev) => ({ ...prev, isActive: e.target.checked }))}
					/>
				</>
			}
			primaryButtonText='Add'
			onPrimaryClick={handleSave}
			onSecondaryClick={onCancel}
			onCloseClick={onCancel}
		/>
	);
};

AddDepartment.propTypes = {
	onSave: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default AddDepartment;
